import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useArticles } from '../hooks/useArticles';
import { useTextSelection } from '../hooks/useTextSelection';
import { useHighlights } from '../hooks/useHighlights';
import { createArticleFeedback, getArticleFeedback } from '../services/articles';
import useNavigation from '../hooks/useNavigation';
import {
  shouldShowLanguageBadge,
  getLanguageName,
  getLanguageBadgeClasses,
  formatLanguageCode,
  getTextDirection
} from '../utils/language';
import { parseMarkdownSummary, hasMarkdownFormatting } from '../utils/markdown';
import { formatFullDate } from '../utils/dateUtils';
import ShareContextMenu from './ShareContextMenu';
import SharePreviewDialog from './SharePreviewDialog';
import RelatedArticlesWidget from './RelatedArticlesWidget';
import ImageDebugPanel from './ImageDebugPanel';
import HighlightsSidebar from './HighlightsSidebar';
import HighlightRenderer from './HighlightRenderer';
import ChatPopup, { ChatFAB } from './ChatPopup';

const ArticleViewer = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [capturedSelection, setCapturedSelection] = useState({
    text: '',
    markdown: '',
    hasMarkdown: false
  });
  const [feedback, setFeedback] = useState(null); // Current feedback state
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [localError, setLocalError] = useState(null); // Local error state for this article
  const [highlightsSidebarOpen, setHighlightsSidebarOpen] = useState(false);
  const [isCreatingHighlight, setIsCreatingHighlight] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { id } = useParams();
  const { goBack } = useNavigation();
  const location = useLocation();

  const {
    currentArticle,
    fetchArticle,
    removeArticle,
    regenerateArticleSummary,
    refetchArticleContent,
    markAsRead,
    markAsUnread,
    isLoading,
    isUpdating,
    isDeleting,
    isRegenerating,
    isRefetching,
    clearCurrentArticle,
    clearOperationStates,
  } = useArticles();

  // Text selection and share functionality
  const {
    selectedText,
    selectedMarkdown,
    hasMarkdown,
    contextMenuVisible,
    contextMenuPosition,
    contextMenuRef,
    hideSelection,
  } = useTextSelection();

  // Highlights functionality
  // NOTE: We pass the raw summary for hash calculation, but we'll use rendered DOM text for positions
  const {
    highlights,
    isLoading: highlightsLoading,
    detachmentInfo,
    createHighlight,
    deleteHighlight,
    deleteAllHighlights,
    reattachHighlights,
  } = useHighlights(parseInt(id), currentArticle?.summary);

  // Fetch article on mount
  useEffect(() => {
    if (id) {
      // Clear local error and operation states from previous article
      setLocalError(null);
      clearOperationStates();

      fetchArticle(parseInt(id)).then((article) => {
        // Debug: Log debug_data status
        console.log('🔬 [ArticleViewer] Article fetched:', {
          id: article?.id,
          hasDebugData: !!article?.debug_data,
          debugDataType: typeof article?.debug_data,
          debugDataLength: Array.isArray(article?.debug_data) ? article.debug_data.length : 'not an array',
          debugDataSample: article?.debug_data ? JSON.stringify(article.debug_data).substring(0, 200) : 'null'
        });

        // Fetch existing feedback after article is loaded
        fetchExistingFeedback(parseInt(id));
      }).catch(error => {
        console.error('Error fetching article:', error);
        setLocalError(error.message);
        if (error.message.includes('404')) {
          goBack();
        }
      });
    }

    // Cleanup on unmount
    return () => {
      clearCurrentArticle();
      clearOperationStates();
      setFeedback(null);
      setLocalError(null);
    };
  }, [id, fetchArticle, goBack, clearCurrentArticle, clearOperationStates]);

  // Clear local errors when switching tabs
  useEffect(() => {
    if (localError) {
      setLocalError(null);
    }
  }, [activeTab]);

  // Scroll position memory (Issue #2 fix)
  // Save scroll position periodically and restore on mount
  useEffect(() => {
    const scrollKey = `article_scroll_${id}`;

    // Restore scroll position if coming back from navigation
    if (location.state?.restoredFromHistory) {
      const savedScroll = sessionStorage.getItem(scrollKey);
      if (savedScroll) {
        const scrollPos = parseInt(savedScroll, 10);
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          window.scrollTo({ top: scrollPos, behavior: 'instant' });
        }, 100);
      }
    }

    // Save scroll position on scroll events (debounced)
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        sessionStorage.setItem(scrollKey, window.scrollY.toString());
      }, 100);
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [id, location.state?.restoredFromHistory]);


  const getDomainFromUrl = (url) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return 'Unknown source';
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await removeArticle(currentArticle.id);
        goBack();
      } catch (error) {
        console.error('Error deleting article:', error);
      }
    }
  };

  const handleMarkAsRead = async () => {
    try {
      await markAsRead(currentArticle.id);
      goBack();
    } catch (error) {
      console.error('Error marking article as read:', error);
    }
  };

  const handleMarkAsReadStay = async () => {
    try {
      await markAsRead(currentArticle.id);
    } catch (error) {
      console.error('Error marking article as read:', error);
    }
  };

  const handleMarkAsUnread = async () => {
    try {
      await markAsUnread(currentArticle.id);
    } catch (error) {
      console.error('Error marking article as unread:', error);
    }
  };


  const handleRegenerateSummary = async () => {
    try {
      setLocalError(null);
      await regenerateArticleSummary(currentArticle.id);
    } catch (error) {
      console.error('Error regenerating summary:', error);
      setLocalError(error.message || 'Failed to regenerate summary');
    }
  };

  const handleRefetchContent = async () => {
    try {
      setLocalError(null);
      await refetchArticleContent(currentArticle.id);
    } catch (error) {
      console.error('Error refetching content:', error);
      setLocalError(error.message || 'Failed to refetch content');
    }
  };

  // Feedback functionality
  const fetchExistingFeedback = async (articleId) => {
    try {
      const feedbackData = await getArticleFeedback(articleId);
      setFeedback(feedbackData);
    } catch (error) {
      // No feedback exists yet, which is fine
      setFeedback(null);
    }
  };

  const handleFeedback = async (feedbackValue) => {
    if (feedbackLoading) return;
    
    setFeedbackLoading(true);
    const previousFeedback = feedback;
    
    // Optimistic update
    setFeedback(prev => ({
      id: prev?.id || Date.now(), // Temporary ID for new feedback
      user_id: prev?.user_id || 0, // Will be set by API response
      article_id: currentArticle.id,
      feedback_value: feedbackValue,
      created_at: new Date().toISOString(),
      ...prev // Keep any existing fields, but override above
    }));

    try {
      const response = await createArticleFeedback(currentArticle.id, feedbackValue);
      setFeedback(response);

      // Toast removed - was causing crashes
    } catch (error) {
      // Revert optimistic update on error
      setFeedback(previousFeedback);
      console.error('Error creating feedback:', error);
      // Toast removed - was causing crashes
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Share functionality handlers
  const handleShareClick = () => {
    // Capture the current selection before clearing it
    setCapturedSelection({
      text: selectedText,
      markdown: selectedMarkdown,
      hasMarkdown: hasMarkdown
    });
    setShareDialogOpen(true);
    hideSelection(); // Clear UI elements but preserve captured data
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    // Clear captured selection when dialog is closed
    setCapturedSelection({
      text: '',
      markdown: '',
      hasMarkdown: false
    });
  };

  // Highlight functionality handlers
  const handleCreateHighlight = async () => {
    // Get the rendered text from the DOM
    const summaryContainer = document.querySelector('[data-markdown-source]');
    const renderedText = summaryContainer?.textContent || '';

    // console.log('[ArticleViewer Debug] summaryContainer element:', summaryContainer);
    // console.log('[ArticleViewer Debug] renderedText length:', renderedText.length);
    // console.log('[ArticleViewer Debug] renderedText (first 300 chars):', renderedText.substring(0, 300));
    // console.log('[ArticleViewer Debug] renderedText (LAST 300 chars):', renderedText.substring(Math.max(0, renderedText.length - 300)));
    // console.log('[ArticleViewer Debug] selectedText:', selectedText);
    // console.log('[ArticleViewer Debug] selectedText length:', selectedText.length);
    // console.log('[ArticleViewer Debug] Does renderedText contain selected?', renderedText.includes(selectedText.substring(0, 20)));

    // Check if the selection is actually within the summary container
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const isWithinContainer = summaryContainer?.contains(range.commonAncestorContainer);
      // console.log('[ArticleViewer Debug] Selection within summary container?', isWithinContainer);

      if (!isWithinContainer) {
        setLocalError('Please select text from the summary to highlight');
        hideSelection();
        return;
      }
    }

    if (!selectedText || !currentArticle?.summary) {
      return;
    }

    try {
      setIsCreatingHighlight(true);
      await createHighlight(selectedText, null, renderedText);
      hideSelection();
    } catch (error) {
      console.error('Error creating highlight:', error);
      setLocalError(error.message || 'Failed to create highlight');
    } finally {
      setIsCreatingHighlight(false);
    }
  };

  const handleHighlightClick = (highlight) => {
    // Scroll to highlight in the article (future enhancement)
    console.log('Highlight clicked:', highlight);
  };

  const handleDeleteHighlight = async (highlightId) => {
    try {
      await deleteHighlight(highlightId);
    } catch (error) {
      console.error('Error deleting highlight:', error);
      setLocalError(error.message || 'Failed to delete highlight');
    }
  };

  const handleReattachHighlights = async () => {
    try {
      await reattachHighlights(0.8);
    } catch (error) {
      console.error('Error re-attaching highlights:', error);
      setLocalError(error.message || 'Failed to re-attach highlights');
    }
  };

  const readingTime = currentArticle?.content ? 
    Math.ceil(currentArticle.content.split(' ').length / 200) : 0;

  // Component for feedback buttons
  const FeedbackButtons = () => {
    return (
      <div className="flex justify-center pt-6 mt-8 border-t-3 border-clay-border">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-600 mr-2">Rate this summary:</span>

          {/* Thumbs Down */}
          <button
            onClick={() => handleFeedback(-1)}
            disabled={feedbackLoading}
            className={`p-2.5 rounded-clay border-2 transition-all duration-200 ${
              feedback?.feedback_value === -1
                ? 'bg-rose-100 border-fun-coral scale-110'
                : 'border-gray-200 hover:border-fun-coral hover:bg-rose-50'
            } ${feedbackLoading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`}
            title="Poor quality"
          >
            {feedbackLoading && feedback?.feedback_value === -1 ? (
              <div className="spinner-fun-sm w-6 h-6"></div>
            ) : (
              <span className="text-2xl">👎</span>
            )}
          </button>

          {/* Neutral */}
          <button
            onClick={() => handleFeedback(0)}
            disabled={feedbackLoading}
            className={`p-2.5 rounded-clay border-2 transition-all duration-200 ${
              feedback?.feedback_value === 0
                ? 'bg-amber-100 border-fun-lemon scale-110'
                : 'border-gray-200 hover:border-fun-lemon hover:bg-amber-50'
            } ${feedbackLoading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`}
            title="Alright"
          >
            {feedbackLoading && feedback?.feedback_value === 0 ? (
              <div className="spinner-fun-sm w-6 h-6"></div>
            ) : (
              <span className="text-2xl">😐</span>
            )}
          </button>

          {/* Thumbs Up */}
          <button
            onClick={() => handleFeedback(1)}
            disabled={feedbackLoading}
            className={`p-2.5 rounded-clay border-2 transition-all duration-200 ${
              feedback?.feedback_value === 1
                ? 'bg-emerald-100 border-fun-mint scale-110'
                : 'border-gray-200 hover:border-fun-mint hover:bg-emerald-50'
            } ${feedbackLoading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`}
            title="Good quality"
          >
            {feedbackLoading && feedback?.feedback_value === 1 ? (
              <div className="spinner-fun-sm w-6 h-6"></div>
            ) : (
              <span className="text-2xl">👍</span>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Component for the "Mark as Read" button at article end
  const MarkReadButton = () => {
    if (currentArticle.is_read) {
      return (
        <div className="flex justify-center pt-8">
          <div className="inline-flex items-center px-4 py-2.5 bg-emerald-50 text-emerald-700
            rounded-clay border-2 border-fun-mint font-medium animate-pop">
            <span className="mr-2 text-lg" role="img" aria-hidden="true">✨</span>
            Article Read
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-center gap-3 pt-8">
        {/* Done Reading - navigates away */}
        <button
          onClick={handleMarkAsRead}
          disabled={isUpdating}
          className="btn-clay-accent inline-flex items-center group"
          title="Mark as read and return to dashboard"
        >
          {isUpdating ? (
            <>
              <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
              Finishing...
            </>
          ) : (
            <>
              <span className="mr-2 text-lg group-hover:animate-bounce-gentle" role="img" aria-hidden="true">🎉</span>
              Done reading!
            </>
          )}
        </button>

        {/* Mark as Read - stays on page */}
        <button
          onClick={handleMarkAsReadStay}
          disabled={isUpdating}
          className="btn-clay-ghost inline-flex items-center justify-center px-3"
          title="Mark as read (stay on page)"
        >
          {isUpdating ? (
            <div className="spinner-fun-sm w-4 h-4"></div>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      </div>
    );
  };

  const tabs = [
    {
      id: 'summary',
      label: 'Summary',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      id: 'content',
      label: 'Content',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
    },
    {
      id: 'original',
      label: 'Original',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-fun-lg mx-auto mb-4"></div>
          <p className="font-display text-lg text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  // Only show full-page error if article failed to load (not during operations)
  if (localError && !currentArticle && !isRegenerating && !isRefetching) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <div className="text-center card-clay-static p-8 max-w-md mx-4">
          <div className="text-5xl mb-4" role="img" aria-label="Error">😅</div>
          <h2 className="font-display text-xl font-bold text-gray-900 mb-2">Oops! Can&apos;t load this article</h2>
          <p className="text-gray-600 mb-6">{localError}</p>
          <button
            onClick={goBack}
            className="btn-clay"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!currentArticle) {
    return null;
  }

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <div className="bg-white border-b-3 border-clay-border shadow-clay-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={goBack}
              className="btn-clay-ghost flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>

            <div className="flex items-center space-x-2">
              {/* Highlights button */}
              <button
                onClick={() => setHighlightsSidebarOpen(true)}
                className="relative p-2.5 text-gray-600 hover:text-fun-lemon hover:bg-amber-50
                  rounded-clay border-2 border-transparent hover:border-fun-lemon
                  transition-all duration-200"
                title="View highlights"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
                {highlights && highlights.length > 0 && (
                  <span className="absolute -top-1 -right-1 badge-fun-lemon text-xs font-bold
                    w-5 h-5 flex items-center justify-center animate-bounce-gentle">
                    {highlights.length}
                  </span>
                )}
              </button>

              {/* Done/Undone button */}
              <button
                onClick={currentArticle.is_read ? handleMarkAsUnread : handleMarkAsRead}
                disabled={isUpdating}
                className={`p-2.5 rounded-clay border-2 transition-all duration-200 ${
                  currentArticle.is_read
                    ? 'text-accent-600 border-accent-300 hover:bg-accent-50 hover:border-accent-400'
                    : 'text-fun-mint border-emerald-300 hover:bg-emerald-50 hover:border-emerald-400'
                }`}
                title={currentArticle.is_read ? "Mark as unread" : "Mark as read"}
              >
                {isUpdating ? (
                  <div className="spinner-fun-sm w-5 h-5"></div>
                ) : currentArticle.is_read ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>

              {/* Delete button */}
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="p-2.5 text-gray-400 hover:text-fun-coral hover:bg-rose-50
                  rounded-clay border-2 border-transparent hover:border-fun-coral
                  transition-all duration-200"
                title="Delete article"
              >
                {isDeleting ? (
                  <div className="spinner-fun-sm w-5 h-5"></div>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner - shown inline when operations fail */}
      {localError && currentArticle && (
        <div className="bg-rose-50 border-b-3 border-fun-coral">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl" role="img" aria-hidden="true">😅</span>
                <p className="text-sm font-medium text-rose-800">{localError}</p>
              </div>
              <button
                onClick={() => setLocalError(null)}
                className="p-1 text-rose-600 hover:text-rose-800 hover:bg-rose-100 rounded-clay transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Article Header */}
      <div className="bg-white border-b-3 border-clay-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-4">
            <div className="flex flex-wrap items-center text-sm text-gray-500 gap-2 mb-2">
              <span className="badge-fun-lavender">
                {getDomainFromUrl(currentArticle.url)}
              </span>
              {/* Read status indicator */}
              {currentArticle.is_read && (
                <span className="badge-fun-mint flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Read
                </span>
              )}
              {/* Language indicator */}
              {shouldShowLanguageBadge(currentArticle.language) && (
                <span
                  className={`${getLanguageBadgeClasses(currentArticle.language)}`}
                  title={`Article language: ${getLanguageName(currentArticle.language)}`}
                >
                  {formatLanguageCode(currentArticle.language)}
                </span>
              )}
              <time dateTime={currentArticle.publish_date || currentArticle.created_at} className="text-gray-500">
                {formatFullDate(currentArticle.publish_date || currentArticle.created_at)}
              </time>
              {readingTime > 0 && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-500">{readingTime} min read</span>
                </>
              )}
            </div>
          </div>

          <h1 className="font-display text-3xl font-bold text-gray-900 mb-4">
            {currentArticle.title || 'Untitled Article'}
          </h1>

          {/* Author, Domain, and Date */}
          {(currentArticle.author || currentArticle.domain || currentArticle.publish_date || currentArticle.created_at) && (
            <div className="flex flex-wrap items-center text-sm text-gray-600 mb-4 gap-4">
              {currentArticle.author && (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{currentArticle.author}</span>
                </div>
              )}
              {currentArticle.domain && (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span>{currentArticle.domain}</span>
                </div>
              )}
              {(currentArticle.publish_date || currentArticle.created_at) && (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{formatFullDate(currentArticle.publish_date || currentArticle.created_at)}</span>
                </div>
              )}
            </div>
          )}

          {/* Status indicator */}
          <div className="flex items-center mb-4">
            {currentArticle.status === 'processing' && (
              <div className="inline-flex items-center px-3 py-1.5 bg-amber-50 border-2 border-fun-lemon rounded-clay">
                <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
                <span className="text-sm font-medium text-amber-700">Processing article...</span>
              </div>
            )}

            {currentArticle.status === 'failed' && (
              <div className="inline-flex items-center px-3 py-1.5 bg-rose-50 border-2 border-fun-coral rounded-clay">
                <span className="mr-2" role="img" aria-hidden="true">😅</span>
                <span className="text-sm font-medium text-rose-700">Failed to process article</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs - Pill Style */}
      <div className="bg-white border-b-3 border-clay-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-2 py-3">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pill-clay flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'pill-clay-active'
                    : ''
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'summary' && (
          <div className="card-clay-static p-8">
            {currentArticle.summary ? (
              <div>
                {/* Re-summarize button */}
                <div className="flex justify-end mb-6">
                  <button
                    onClick={handleRegenerateSummary}
                    disabled={isRegenerating}
                    className="btn-clay-ghost inline-flex items-center"
                    title="Generate a new summary for this article"
                  >
                    {isRegenerating ? (
                      <>
                        <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
                        Regenerating...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Re-summarize
                      </>
                    )}
                  </button>
                </div>
                
                {/* Summary content with highlight overlay */}
                <HighlightRenderer
                  text={null}
                  highlights={highlights}
                  onHighlightClick={handleHighlightClick}
                >
                  <div
                    className="prose prose-lg max-w-none"
                    dir={getTextDirection(currentArticle.language)}
                    data-markdown-source={currentArticle.summary}
                    data-markdown={currentArticle.summary}
                  >
                    {hasMarkdownFormatting(currentArticle.summary) ? (
                      <div className="space-y-2">
                        {parseMarkdownSummary(currentArticle.summary)}
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                        {currentArticle.summary}
                      </div>
                    )}
                  </div>
                </HighlightRenderer>

                {/* Image Debug Panel (dev only) */}
                {currentArticle.debug_data && (
                  <ImageDebugPanel
                    debugData={currentArticle.debug_data}
                    threshold={0.08}
                  />
                )}

                {/* Feedback buttons */}
                <FeedbackButtons />

                {/* Mark as read button */}
                <MarkReadButton />

                {/* Related Articles Widget */}
                <RelatedArticlesWidget
                  articleId={parseInt(id)}
                  currentArticle={currentArticle}
                />
              </div>
            ) : (
              <div>
                <div className="text-center py-12">
                  <div className="text-5xl mb-4" role="img" aria-label="No summary">📝</div>
                  <p className="font-display text-lg font-medium text-gray-700 mb-4">No summary available yet</p>
                  {currentArticle.status === 'processing' ? (
                    <p className="text-sm text-gray-500 mt-2">Summary will be generated shortly...</p>
                  ) : (
                    <button
                      onClick={handleRegenerateSummary}
                      disabled={isRegenerating}
                      className="btn-clay-accent inline-flex items-center"
                    >
                      {isRegenerating ? (
                        <>
                          <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Generate Summary
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Feedback buttons - available even without summary */}
                <FeedbackButtons />

                {/* Mark as read button */}
                <MarkReadButton />
              </div>
            )}
          </div>
        )}

        {activeTab === 'content' && (
          <div className="card-clay-static p-8">
            {currentArticle.content ? (
              <div>
                {/* Re-fetch content button */}
                <div className="flex justify-end mb-6">
                  <button
                    onClick={handleRefetchContent}
                    disabled={isRefetching}
                    className="btn-clay-ghost inline-flex items-center"
                    title="Re-fetch the latest content from the original URL"
                  >
                    {isRefetching ? (
                      <>
                        <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
                        Re-fetching...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Re-fetch Content
                      </>
                    )}
                  </button>
                </div>
                
                {/* Content display */}
                <div 
                  className="prose prose-lg max-w-none"
                  dir={getTextDirection(currentArticle.language)}
                  data-markdown-source={currentArticle.content}
                  data-markdown={currentArticle.content}
                >
                  {hasMarkdownFormatting(currentArticle.content) ? (
                    <div className="space-y-2">
                      {parseMarkdownSummary(currentArticle.content)}
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                      {currentArticle.content}
                    </div>
                  )}
                </div>

                {/* Feedback buttons */}
                <FeedbackButtons />

                {/* Mark as read button */}
                <MarkReadButton />

                {/* Related Articles Widget */}
                <RelatedArticlesWidget
                  articleId={parseInt(id)}
                  currentArticle={currentArticle}
                />
              </div>
            ) : (
              <div>
                <div className="text-center py-12">
                  <div className="text-5xl mb-4" role="img" aria-label="No content">📖</div>
                  <p className="font-display text-lg font-medium text-gray-700 mb-4">No content available yet</p>
                  {currentArticle.status === 'processing' ? (
                    <p className="text-sm text-gray-500 mt-2">Content is being extracted...</p>
                  ) : (
                    <button
                      onClick={handleRefetchContent}
                      disabled={isRefetching}
                      className="btn-clay-accent inline-flex items-center"
                    >
                      {isRefetching ? (
                        <>
                          <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
                          Fetching...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Fetch Content
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Feedback buttons - available even without content */}
                <FeedbackButtons />

                {/* Mark as read button */}
                <MarkReadButton />
              </div>
            )}
          </div>
        )}

        {activeTab === 'original' && (
          <div className="card-clay-static p-8">
            <div className="text-center">
              <div className="mb-6">
                <div className="text-5xl mb-4" role="img" aria-label="External link">🔗</div>
                <h3 className="font-display text-xl font-bold text-gray-900 mb-2">
                  View Original Article
                </h3>
                <p className="text-gray-600 mb-6">
                  Visit the original source to read the full article with all formatting, images, and interactive elements.
                </p>
              </div>

              <a
                href={currentArticle.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-clay-accent inline-flex items-center text-base"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open Original Article
              </a>

              <div className="mt-4 text-sm text-gray-500 bg-gray-50 p-3 rounded-clay border-2 border-gray-200">
                <span className="break-all">{currentArticle.url}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Share and Highlight Components */}
      {selectedText && (
        <>
          {/* Context Menu (for right-click) */}
          <ShareContextMenu
            visible={contextMenuVisible}
            position={contextMenuPosition}
            onShare={handleShareClick}
            onHighlight={handleCreateHighlight}
            onClose={hideSelection}
            hasMarkdown={hasMarkdown}
            showHighlight={activeTab === 'summary'}
            isHighlighting={isCreatingHighlight}
            ref={contextMenuRef}
          />
        </>
      )}

      {/* Share Preview Dialog */}
      <SharePreviewDialog
        isOpen={shareDialogOpen}
        onClose={handleCloseShareDialog}
        selectedText={capturedSelection.text}
        selectedMarkdown={capturedSelection.markdown}
        hasMarkdown={capturedSelection.hasMarkdown}
        articleTitle={currentArticle?.title}
        articleUrl={currentArticle?.url}
      />

      {/* Processing Toast - non-blocking notification */}
      {(isRegenerating || isRefetching) && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up-bounce">
          <div className="card-clay-sm p-4 max-w-sm">
            <div className="flex items-start gap-3">
              <div className="spinner-fun-sm w-6 h-6 flex-shrink-0"></div>
              <div className="flex-1">
                <h4 className="font-display text-sm font-bold text-gray-900 mb-1">
                  {isRefetching ? 'Re-fetching Content...' : 'Regenerating Summary...'}
                </h4>
                <p className="text-xs text-gray-600">
                  {isRefetching
                    ? 'This may take 1-5 minutes. Feel free to navigate away.'
                    : 'This may take 30-120 seconds. Feel free to navigate away.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Highlights Sidebar */}
      <HighlightsSidebar
        highlights={highlights}
        isOpen={highlightsSidebarOpen}
        onClose={() => setHighlightsSidebarOpen(false)}
        onHighlightClick={handleHighlightClick}
        onDeleteHighlight={handleDeleteHighlight}
        onDeleteAll={deleteAllHighlights}
        detachmentInfo={detachmentInfo}
        onReattach={handleReattachHighlights}
        isLoading={highlightsLoading}
      />

      {/* Chat FAB and Popup */}
      {currentArticle && currentArticle.summary && (
        <>
          <ChatFAB
            onClick={() => setChatOpen(!chatOpen)}
            isOpen={chatOpen}
          />
          <ChatPopup
            isOpen={chatOpen}
            onClose={() => setChatOpen(false)}
            contextType="article"
            contextId={currentArticle.id}
            contextTitle={currentArticle.title || 'Article'}
          />
        </>
      )}
    </div>
  );
};

export default ArticleViewer;